/**
 * ScrollingSlider — pins the section while the slide track scrolls sideways.
 *
 * Layout:
 *   <blfc-scrolling-slider>
 *     .blfc-slider__spacer   tall enough to fund the horizontal travel
 *       .blfc-slider__pin    position: sticky, overflow: hidden  <- clips here
 *         .blfc-slider__track  overflow: visible, translated on X
 *
 * While pinned the pin fills the viewport below the header, so the slides
 * cover the screen and no section background shows above or below them. Its
 * top and height come from the section's "Header offset" setting via the
 * `--blfc-slider-header-offset` custom property, so CSS owns the geometry and
 * this file only reads it back for the scroll maths.
 *
 * The spacer is `pinHeight + overflow` tall, so one pixel of page scroll maps
 * to exactly one pixel of horizontal travel. Scrolling back up reverses
 * through the same mapping with no separate code path.
 *
 * Touch devices and `prefers-reduced-motion` never pin — they keep the base
 * CSS, which is a native horizontal scroller at the section's Height setting.
 * That is also the no-JS state.
 */

/**
 * Fallback used only if `--blfc-slider-header-offset` cannot be read. Keep in
 * step with the default on the section's "Header offset" setting.
 */
const DEFAULT_HEADER_OFFSET_PX = 60;

class BlfcScrollingSlider extends HTMLElement {
  connectedCallback() {
    this.spacer = this.querySelector('.blfc-slider__spacer');
    this.pin = this.querySelector('.blfc-slider__pin');
    this.track = this.querySelector('.blfc-slider__track');
    if (!this.spacer || !this.pin || !this.track) return;

    this.overflow = 0;
    this.headerOffset = DEFAULT_HEADER_OFFSET_PX;
    this.ticking = false;

    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onModeChange = this.onModeChange.bind(this);

    this.pointerQuery.addEventListener('change', this.onModeChange);
    this.motionQuery.addEventListener('change', this.onModeChange);
    window.addEventListener('resize', this.onResize, { passive: true });

    // Observe the track only — it changes when slides load or widths change.
    // The pin is sized purely by CSS from the viewport, which the window
    // resize listener above already covers.
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.track);

    this.applyMode();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.pointerQuery) this.pointerQuery.removeEventListener('change', this.onModeChange);
    if (this.motionQuery) this.motionQuery.removeEventListener('change', this.onModeChange);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /** Pin only for fine pointers, and never when reduced motion is requested. */
  get shouldPin() {
    return this.pointerQuery.matches && !this.motionQuery.matches;
  }

  onModeChange() {
    this.applyMode();
  }

  applyMode() {
    if (this.shouldPin) {
      this.enablePin();
    } else {
      this.disablePin();
    }
  }

  enablePin() {
    this.classList.add('is-pinned');
    this.measure();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  disablePin() {
    this.classList.remove('is-pinned');
    window.removeEventListener('scroll', this.onScroll);
    this.spacer.style.height = '';
    this.track.style.transform = '';
  }

  /** The section's "Header offset" setting, in px. */
  readHeaderOffset() {
    const raw = window.getComputedStyle(this).getPropertyValue('--blfc-slider-header-offset');
    const value = parseFloat(raw);
    return Number.isNaN(value) ? DEFAULT_HEADER_OFFSET_PX : value;
  }

  measure() {
    // Cached rather than read per frame — update() runs on every rAF.
    this.headerOffset = this.readHeaderOffset();

    // CSS sizes the pin from the same setting; read the result back instead of
    // recomputing it here, so the two can never disagree.
    const pinHeight = this.pin.offsetHeight;

    // The track is `width: max-content` while pinned, so scrollWidth is the
    // true width of all slides laid end to end.
    this.overflow = Math.max(0, this.track.scrollWidth - this.pin.clientWidth);

    // Fund exactly as much page scroll as there is horizontal travel.
    this.spacer.style.height = `${pinHeight + this.overflow}px`;
  }

  onResize() {
    if (!this.shouldPin) return;
    this.measure();
    this.update();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    if (!this.overflow) return;

    const rect = this.spacer.getBoundingClientRect();

    // Skip while off screen.
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;

    // 0 the moment the pin sticks, 1 when the spacer has been consumed.
    const travelled = this.headerOffset - rect.top;
    const progress = Math.min(1, Math.max(0, travelled / this.overflow));

    this.track.style.transform = `translate3d(${-progress * this.overflow}px, 0, 0)`;
  }
}

if (!customElements.get('blfc-scrolling-slider')) {
  customElements.define('blfc-scrolling-slider', BlfcScrollingSlider);
}
